import React, { useCallback } from 'react';
import { Calculator, Layers, BarChart2 } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров 3D-печати
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  materialTypes,
  printerTypes
}) => {
  // Расчет объема модели
  const modelVolume = parseFloat(formData.modelVolume) || 0;
  
  // Обработчик изменения объема модели
  const handleModelVolumeChange = useCallback((e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      modelVolume: newValue
    }));
  }, [setFormData]);
  
  // Обработчик изменения высоты модели
  const handleModelHeightChange = useCallback((e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      modelHeight: newValue
    }));
  }, [setFormData]);
  
  // Обработчик изменения количества
  const handleQuantityChange = useCallback((e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      printQuantity: newValue
    }));
  }, [setFormData]);
  
  // Обработчик изменения плотности заполнения
  const handleInfillDensityChange = useCallback((e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      infillDensity: newValue
    }));
  }, [setFormData]);
  
  // Предопределенные объемы модели
  const predefinedVolumes = [
    { value: '10', label: '10 см³' },
    { value: '50', label: '50 см³' },
    { value: '100', label: '100 см³' },
    { value: '500', label: '500 см³' }
  ];
  
  // Предопределенные высоты модели
  const predefinedHeights = [
    { value: '5', label: '5 см' },
    { value: '10', label: '10 см' },
    { value: '15', label: '15 см' },
    { value: '20', label: '20 см' }
  ];
  
  // Предопределенные количества
  const predefinedQuantities = [
    { value: '1', label: '1 шт.' },
    { value: '5', label: '5 шт.' },
    { value: '10', label: '10 шт.' },
    { value: '50', label: '50 шт.' }
  ];
  
  // Предопределенные значения заполнения
  const predefinedInfillDensities = [
    { value: '10', label: '10%' },
    { value: '20', label: '20%' },
    { value: '50', label: '50%' },
    { value: '100', label: '100%' }
  ];
  
  // Функция расчета
  const calculateCost = () => {
    // Здесь должна быть вызвана функция основного расчета
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-teal-500" />
        Быстрая настройка параметров
      </h3>
      
      <div className="space-y-5">
        {/* Объем модели */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="modelVolumeSlider" className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
              <BarChart2 className="w-4 h-4 text-teal-500" />
              Объем модели
            </label>
            <span className="font-medium text-gray-900 dark:text-white">{modelVolume} см³</span>
          </div>
          <input
            type="range"
            id="modelVolumeSlider"
            min="1"
            max="1000"
            step="1"
            value={modelVolume}
            onChange={handleModelVolumeChange}
            aria-label="Объем модели"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-teal-600"
          />
          
          {/* Кнопки с предопределенными объемами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedVolumes.map(volume => (
              <button
                key={volume.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, modelVolume: volume.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.modelVolume === volume.value 
                    ? 'bg-teal-100 border-teal-300 text-teal-700 dark:bg-teal-900/30 dark:border-teal-700 dark:text-teal-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {volume.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Высота модели */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="modelHeightSlider" className="text-gray-600 dark:text-gray-400">
              Высота модели
            </label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.modelHeight} см
            </span>
          </div>
          <input
            type="range"
            id="modelHeightSlider"
            min="0.5"
            max="30"
            step="0.5"
            value={formData.modelHeight}
            onChange={handleModelHeightChange}
            aria-label="Высота модели (см)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-teal-600"
          />
          
          {/* Кнопки с предопределенными высотами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedHeights.map(height => (
              <button
                key={height.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, modelHeight: height.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.modelHeight === height.value 
                    ? 'bg-teal-100 border-teal-300 text-teal-700 dark:bg-teal-900/30 dark:border-teal-700 dark:text-teal-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {height.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Количество */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="printQuantitySlider" className="text-gray-600 dark:text-gray-400">
              Количество изделий
            </label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.printQuantity} шт.
            </span>
          </div>
          <input
            type="range"
            id="printQuantitySlider"
            min="1"
            max="100"
            step="1"
            value={formData.printQuantity}
            onChange={handleQuantityChange}
            aria-label="Количество изделий"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-teal-600"
          />
          
          {/* Кнопки с предопределенными количествами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedQuantities.map(quantity => (
              <button
                key={quantity.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, printQuantity: quantity.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.printQuantity === quantity.value 
                    ? 'bg-teal-100 border-teal-300 text-teal-700 dark:bg-teal-900/30 dark:border-teal-700 dark:text-teal-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {quantity.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Плотность заполнения */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="infillDensitySlider" className="text-gray-600 dark:text-gray-400">
              Плотность заполнения
            </label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.infillDensity}%
            </span>
          </div>
          <input
            type="range"
            id="infillDensitySlider"
            min="0"
            max="100"
            step="5"
            value={formData.infillDensity}
            onChange={handleInfillDensityChange}
            aria-label="Плотность заполнения (%)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-teal-600"
          />
          
          {/* Кнопки с предопределенными плотностями */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedInfillDensities.map(density => (
              <button
                key={density.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, infillDensity: density.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.infillDensity === density.value 
                    ? 'bg-teal-100 border-teal-300 text-teal-700 dark:bg-teal-900/30 dark:border-teal-700 dark:text-teal-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {density.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Выбор материала */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
              <Layers className="w-4 h-4 text-teal-500" />
              Тип материала
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {materialTypes.slice(0, 6).map(material => (
              <button
                key={material.id}
                type="button"
                onClick={() => setFormData(prev => ({ 
                  ...prev, 
                  materialType: material.id,
                  materialDensity: material.density.toString(),
                  materialCostPerKg: material.costPerKg.toString()
                }))}
                className={`p-2 text-sm rounded-lg border transition-colors
                  ${formData.materialType === material.id 
                    ? 'bg-teal-100 border-teal-300 text-teal-700 dark:bg-teal-900/30 dark:border-teal-700 dark:text-teal-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <div className="font-medium">{material.name}</div>
                <div className="text-xs opacity-75">{material.costPerKg} ₽/кг</div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Выбор принтера */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
              <Calculator className="w-4 h-4 text-teal-500" />
              Тип принтера
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {printerTypes.slice(0, 4).map(printer => (
              <button
                key={printer.id}
                type="button"
                onClick={() => setFormData(prev => ({ 
                  ...prev, 
                  printerType: printer.id,
                  printerCost: printer.avgCost.toString(),
                  printerConsumption: printer.powerConsumption.toString()
                }))}
                className={`p-2 text-sm rounded-lg border transition-colors
                  ${formData.printerType === printer.id 
                    ? 'bg-teal-100 border-teal-300 text-teal-700 dark:bg-teal-900/30 dark:border-teal-700 dark:text-teal-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <div className="font-medium">{printer.id.toUpperCase()}</div>
                <div className="text-xs opacity-75 truncate">{printer.name.split('(')[0]}</div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateCost}
          icon={<Calculator className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать стоимость
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;